import React, { useEffect } from "react";
import { Constants } from '@shared';
import {
    CommunityLogo,
    Spinner,
    GuarantorRoute
} from '@common';
import { Route, Routes } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';

import {
    AddPayors,
    AutoPayConfirmation,
    AutoPayWarning,
    CheckEmail,
    CheckSession,
    ConfirmPayment,
    Congratulations,
    DefaultPaymentMethod,
    PaymentMethodSelector,
    EnrollmentCompleted,
    Guarantor,
    HomeSelector,
    InformationSafety,
    ManageEnrolledPayors,
    ManagePayors,
    MyAccount,
    NotFound,
    PaymentAutopay,
    PaymentHistory,
    PaymentMethodSettings,
    Payor,
    Signup,
    SignupCompleted,
    StandAloneAutoEnrollent,
    StepAutoEnrollent,
    StepAutoPay,
    StepBeginEnrollament,
    StepEnrollmentPayors,
    StepEnrollmentSummary,
    StepUpdateEnrollmentSummary,
    StepsQualification,
    StepsProduct,
    UserLogin,
    Welcome,
    ProductSelector,
    QualificationInfo,
    PaymentMethodOptions,
    ForgotPassword,
    ForgotPasswordInfo,
    ResetPassword,
    ResetPasswordSuccess,
    ResendWelcome,
    ResendWelcomeInfo,
    MakePayment,
    Menu,
    OnboardMultipleAccountsInfo,
    ExistingPaymentMethods,
    MultiAutopay,
    ResolveDefaultPaymentMethods
} from "@components";
import { LogoFooter } from "@resources";

const App = () => {

    const navigate = useNavigate(), location = useLocation();

    useEffect(() => {
        if (analytics) {
            analytics?.page();
        }
    }, [location]);

    return (
        <div className="container mx-auto max-w-xl">
            <div className="flex flex-col mt-4 mb-4 bg-white shadow-sb rounded-xl">
                <header className="flex flex-row p-3 rounded-t-xl place-items-end justify-between bg-sb-bg">
                    <CommunityLogo className="h-20 ml-5 flex justify-left items-end cursor-pointer" onClick={() => navigate(`/`)} />
                    <div className="mr-3">
                        <Menu />
                    </div>
                </header>
                <div className="bg-blue-over h-1"></div>
                <main>
                    <Spinner />
                    <Routes>
                        <Route path="/login" element={<UserLogin />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/forgot-password-info" element={<ForgotPasswordInfo />} />
                        <Route path="/reset-password/:sessionId" element={<ResetPassword />} />
                        <Route path="/reset-password-success" element={<ResetPasswordSuccess />} />
                        <Route path="/resend-welcome" element={<ResendWelcome />} />
                        <Route path="/resend-welcome-info" element={<ResendWelcomeInfo />} />

                        <Route path="/signup/:sessionId" element={<Signup />} />

                        <Route element={<CheckSession redirectPath="/login" />}>
                            <Route path="/" element={<HomeSelector />} />
                            <Route path="/myaccount" element={<MyAccount />} />
                            <Route path="/payment-method-settings" element={<PaymentMethodSettings />} />
                            <Route path="/manage-payors" element={<ManagePayors />} />
                            <Route path="/manage-enrolled-payors" element={<ManageEnrolledPayors />} />
                            <Route path="/payment-history" element={<PaymentHistory />} />

                            <Route path="/autopay-warning" element={<AutoPayWarning />} />
                            <Route path="/autopay-confirmation" element={<AutoPayConfirmation />} />
                            <Route path="/multi-autopay" element={<MultiAutopay />} />

                            <Route path="/resolve-defaults" element={<ResolveDefaultPaymentMethods />} />

                            <Route path="/make-payment" element={<MakePayment />} />
                            <Route path="/confirm-payment" element={<ConfirmPayment />} />
                            <Route path="/default-payment-method" element={<DefaultPaymentMethod />} />
                            <Route path="/payment-method-selector" element={<PaymentMethodSelector />} />
                            <Route path="/payment-method-options" element={<PaymentMethodOptions />} />

                            <Route path="/product" element={<ProductSelector />} />

                            <Route path="/add-payors" element={<AddPayors />} />
                            <Route element={<GuarantorRoute />}>
                                <Route path="/guarantor" element={<Guarantor />} />
                            </Route>

                            <Route path="/payor" element={<Payor />} />

                            <Route path="/payment-autopay" element={<PaymentAutopay />} />
                            <Route path="/confirm-autopay" element={<StepAutoPay />} />

                            <Route path="/welcome" element={<Welcome />} />
                            <Route path="/information-safety" element={<InformationSafety />} />
                            <Route path="/qualify-explain" element={<StepsQualification />} />
                            <Route path="/qualification-info" element={<QualificationInfo />} />
                            <Route path="/guarantor/begin-enrollment" element={<StepBeginEnrollament />} />

                            <Route path="/guarantor/step-auto-enroll" element={<StepAutoEnrollent />} />
                            <Route path="/guarantor/auto-enroll" element={<StandAloneAutoEnrollent />} />
                            <Route path="/guarantor/invoice/payors" element={<StepEnrollmentPayors />} />
                            <Route path="/guarantor/multiple-accounts-info" element={<OnboardMultipleAccountsInfo />} />

                            <Route path="/guarantor/invoice/summary" element={<StepEnrollmentSummary />} />
                            <Route path="/update-enrollment/summary" element={<StepUpdateEnrollmentSummary />} />
                            <Route path="/existing-payment-methods" element={<ExistingPaymentMethods />} />

                            <Route path="/guarantor/invoice/enrollment-completed" element={<EnrollmentCompleted />} />
                            <Route path="/signup-completed" element={<SignupCompleted />} />

                            <Route path="/steps" element={<StepsProduct />} />
                            <Route path="/congratulations" element={<Congratulations />} />


                        </Route>


                        <Route path="/check-email" element={<CheckEmail />} />
                        <Route path="/stage" element={<Welcome />} />
                        <Route path="/*" element={<NotFound />} />
                    </Routes>

                </main>
                <footer className="flex flex-col bg-sb-bg p-3 pb-8 gap-y-4 rounded-b-xl text-blue-text">
                    <div className="flex flex-row justify-between">
                        <div className="ml-5 mt-3">
                            <LogoFooter className="h-6" />
                            <span className="text-sm">All rights reserved. Sunbound 2023</span>
                        </div>
                        <div className="flex flex-col items-end underline text-sm mr-5">
                            <div><a href={Constants.Help.FAQ} target="_blank" rel="noreferrer">FAQ</a></div>
                            <div><a href={Constants.Help.Privacy} target="_blank" rel="noreferrer">Privacy Policy</a></div>
                            <div><a href={`mailto:${Constants.Help.Email}?subject=Help with Sunbound Pay`}>Support Email</a></div>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );

};

export default App;
